/**
 * Turning a market median into a pay range, in the browser.
 *
 * **A second copy of `backend/src/lib/bands.ts`, because the frontend does not
 * import backend code. Change both.** `TC-SB-034` runs the two against the
 * same inputs and fails if they disagree, so the copies cannot drift in
 * silence the way the last two did.
 *
 * The last time this mock was allowed to reason its own way about a band it
 * derived one from the role's own pay range, agreed with itself, and hid a
 * compa-ratio that was a thousand times out.
 */

/** How wide a range is when a company has not said. */
export const DEFAULT_SPREAD = 10

/** The widest a range may be, as a percentage each way. */
export const MOST_SPREAD = 50

/** A range, in whole currency, as the survey reported it. */
export interface Band {
  minimum: number
  midpoint: number
  maximum: number
}

/** A median the market is proposing, which nobody has accepted yet. */
export interface Waiting {
  from: number
  to: number
}

export const spreadIsSane = (spread: unknown): spread is number =>
  typeof spread === 'number'
  && Number.isFinite(spread)
  && spread >= 0
  && spread <= MOST_SPREAD

/**
 * The product without its floating point noise.
 *
 * `100000 * 1.1` is `110000.00000000001`, and a maximum that rounds up turns
 * that into 110,001. A range of 90,000 to 110,001 around a round hundred
 * thousand reads as a typo.
 */
const exact = (n: number) => Math.round(n * 1e6) / 1e6

/**
 * The range for one median at one spread.
 *
 * The midpoint is the median untouched. The minimum rounds down and the
 * maximum rounds up, so rounding never narrows a range and never refuses an
 * offer it should have allowed.
 */
export function bandFor(median: number, spread: number): Band {
  const width = spread / 100
  return {
    minimum: Math.floor(exact(median * (1 - width))),
    midpoint: median,
    maximum: Math.ceil(exact(median * (1 + width))),
  }
}

/**
 * The median a range is actually built on.
 *
 * A new benchmark proposes, it does not impose. The first median is the
 * exception and is taken as it arrives, because a range nobody has seen
 * cannot have been quoted.
 */
export const accepted = (
  held: { median: number } | undefined,
  market: number | null,
): number | null => (held ? held.median : market)

/**
 * What this role is waiting for a person to accept, or null.
 *
 * Null in four cases: nothing has ever been accepted, the survey now covers
 * nothing, the market agrees with what is held, or the market has moved back
 * to it.
 */
export function waitingOn(
  held: { median: number } | undefined,
  market: number | null,
): Waiting | null {
  if (!held || market === null) return null
  if (held.median === market) return null
  return { from: held.median, to: market }
}

/** The width that applies to one role. An override of zero is a real choice. */
export function spreadFor(
  companySpread: number | undefined,
  roleSpread: number | undefined,
): { spread: number; overridden: boolean } {
  if (spreadIsSane(roleSpread)) return { spread: roleSpread, overridden: true }
  if (spreadIsSane(companySpread)) return { spread: companySpread, overridden: false }
  return { spread: DEFAULT_SPREAD, overridden: false }
}

/**
 * The percentiles a benchmark can hold, lowest first.
 *
 * Four, and no others. The 65th a prototype once offered came from
 * interpolating between two of these, which puts a figure in a pay range that
 * no provider ever stated.
 */
export const PCTS = ['p25', 'p50', 'p75', 'p90'] as const
export type Pct = typeof PCTS[number]

/** The default, and what nearly every role stays on. */
export const DEFAULT_PCT: Pct = 'p50'

/** Whether a percentile from outside is one a benchmark can hold. */
export const isPct = (v: unknown): v is Pct => (PCTS as readonly unknown[]).includes(v)

/** How a percentile is written on a screen or in a file. */
export const pctLabel = (p: Pct): string =>
  ({ p25: '25th', p50: '50th', p75: '75th', p90: '90th' })[p]

/** A thing with the four percentiles on it, however it was built. */
type Figures = { p25: number | null; p50: number; p75: number | null; p90: number | null } | null

/**
 * The percentiles this role can actually be centred on.
 *
 * Built from the data, not from a list. A real survey fills in some of the
 * four and leaves the rest empty, and which ones differ by role.
 */
export const offeredPcts = (band: Figures | undefined): Pct[] =>
  band ? PCTS.filter((p) => typeof band[p] === 'number') : []

/**
 * The market figure a range is built on, at one percentile, or null.
 *
 * Nothing is interpolated and nothing falls back. A percentile the survey left
 * empty returns null rather than its neighbour's figure.
 */
export const figureAt = (band: Figures | undefined, pct: Pct): number | null =>
  band?.[pct] ?? null

/**
 * The percentile a role is centred on.
 *
 * A role that has never chosen is on the median, and so is one whose stored
 * choice is no longer reported: a survey can be replaced by one publishing
 * less, and a range centred on a figure that has gone has no midpoint at all.
 */
export function pctFor(chosen: string | undefined, offered: Pct[]): Pct {
  if (isPct(chosen) && offered.includes(chosen)) return chosen
  return DEFAULT_PCT
}
